import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { type Step, Stepper } from "@/components/ui/stepper";
import {
  type TransactionFormData,
  TYPES_NEEDING_DIRECT_POINTS,
  transactionFormSchema,
} from "../transaction-form-schema";
import { StepConfirmation } from "./step-confirmation";
import { StepDetails } from "./step-details";
import { StepSuccess } from "./step-success";
import { useCreateTransactionWizard } from "./use-create-transaction-wizard";

const defaultFormValues: TransactionFormData = {
  user: null,
  station: null,
  transaction_type: "purchase",
  purchase_value_cents: 0,
  points: 0,
  description: "",
};

interface CreateTransactionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTransactionSheet({ open, onOpenChange }: CreateTransactionSheetProps) {
  const { t } = useTranslation();
  const wizard = useCreateTransactionWizard();
  const { setCalculationParams } = wizard;
  const prevOpenRef = useRef(open);

  const wizardSteps: Step[] = useMemo(
    () => [
      { id: "details", label: t("transactions.create.steps.details") },
      { id: "confirmation", label: t("transactions.create.steps.confirmation") },
      { id: "success", label: t("transactions.create.steps.success") },
    ],
    [t],
  );

  const form = useForm<TransactionFormData>({
    resolver: zodResolver(transactionFormSchema),
    defaultValues: defaultFormValues,
    mode: "onChange",
  });

  // Watch form values at sheet level for calculation sync
  const transactionType = form.watch("transaction_type");
  const purchaseValueCents = form.watch("purchase_value_cents");
  const points = form.watch("points");
  const selectedUser = form.watch("user");

  // Track previous transaction type for field reset logic
  const prevTransactionTypeRef = useRef(transactionType);

  // Keep wizard calculation params in sync with form values (regardless of current step)
  useEffect(
    function syncCalculationParams() {
      const loyaltyAccountId = selectedUser?.loyalty_account?.id ?? 0;
      const needsDirectPointsInput = TYPES_NEEDING_DIRECT_POINTS.includes(
        transactionType as (typeof TYPES_NEEDING_DIRECT_POINTS)[number],
      );

      setCalculationParams({
        loyaltyAccountId,
        purchaseValueCents,
        transactionType,
        points: needsDirectPointsInput ? points : undefined,
      });
    },
    [selectedUser, transactionType, purchaseValueCents, points, setCalculationParams],
  );

  // Reset fields when transaction type changes
  useEffect(
    function resetFieldsOnTypeChange() {
      if (prevTransactionTypeRef.current !== transactionType) {
        form.setValue("purchase_value_cents", 0);
        form.setValue("points", 0);
        prevTransactionTypeRef.current = transactionType;
      }
    },
    [transactionType, form],
  );

  // Reset wizard and form when sheet closes (transition from open to closed)
  useEffect(
    function resetOnClose() {
      if (prevOpenRef.current && !open) {
        wizard.reset();
        form.reset(defaultFormValues);
      }
      prevOpenRef.current = open;
    },
    [open, form.reset, wizard.reset],
  );

  function handleCancel() {
    onOpenChange(false);
  }

  function handleContinueToConfirmation() {
    wizard.goToNext();
  }

  function handleBackToDetails() {
    wizard.goToPrevious();
  }

  function handleTransactionSuccess() {
    wizard.goToNext();
  }

  function handleClose() {
    onOpenChange(false);
  }

  function handleCreateAnother() {
    form.reset(defaultFormValues);
    wizard.reset();
  }

  const stepTitle = useMemo(
    function getStepTitle() {
      switch (wizard.currentStep) {
        case "details":
          return t("transactions.create.new_transaction");
        case "confirmation":
          return t("transactions.create.confirm_transaction");
        case "success":
          return t("transactions.create.transaction_complete");
        default:
          return t("transactions.create.new_transaction");
      }
    },
    [wizard.currentStep, t],
  );

  const stepDescription = useMemo(
    function getStepDescription() {
      switch (wizard.currentStep) {
        case "details":
          return t("transactions.create.enter_details");
        case "confirmation":
          return t("transactions.create.review_confirm");
        case "success":
          return t("transactions.create.transaction_created");
        default:
          return "";
      }
    },
    [wizard.currentStep, t],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle>{stepTitle}</SheetTitle>
          <SheetDescription>{stepDescription}</SheetDescription>
          <Stepper steps={wizardSteps} currentStep={wizard.currentStep} className="pt-4" />
        </SheetHeader>

        <SheetBody className="flex-1 flex flex-col">
          <FormProvider {...form}>
            <div className="flex-1 flex flex-col">
              {wizard.currentStep === "details" && (
                <StepDetails onCancel={handleCancel} onContinue={handleContinueToConfirmation} />
              )}
              {wizard.currentStep === "confirmation" && (
                <StepConfirmation
                  wizard={wizard}
                  onBack={handleBackToDetails}
                  onSuccess={handleTransactionSuccess}
                />
              )}
              {wizard.currentStep === "success" && (
                <StepSuccess
                  wizard={wizard}
                  onClose={handleClose}
                  onCreateAnother={handleCreateAnother}
                />
              )}
            </div>
          </FormProvider>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
